import Axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { createClient } from '../../graphql/client';
import { RefreshTokenDocument } from '@cyren/common-lib';
import envs from '../../common/envs';

// 토큰 저장소 (TokenManager에서 관리)
export const GlobalAuthInfo: { AccessToken?: string } = {
  AccessToken: '',
};

// Axios 인스턴스 생성 (V2 REST API 전용)
export const AXIOS_INSTANCE = Axios.create({
  baseURL: envs.v2BackendUrl,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 401 에러 핸들링 (토큰 자동 갱신)
AXIOS_INSTANCE.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

    // 401 에러이고, 재시도하지 않은 경우
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // GraphQL RefreshToken 뮤테이션으로 토큰 갱신
        const client = createClient();
        const result = await client.mutate({
          mutation: RefreshTokenDocument,
        });

        const newToken = result.data?.refreshToken?.jwt;
        if (newToken) {
          // 새 토큰 저장
          GlobalAuthInfo.AccessToken = newToken;

          // 원래 요청에 새 토큰 적용
          if (!originalRequest.headers) {
            originalRequest.headers = {};
          }
          originalRequest.headers.Authorization = `Bearer ${newToken}`;

          // 원래 요청 재시도
          return AXIOS_INSTANCE(originalRequest);
        }
      } catch (refreshError) {
        // 토큰 갱신 실패 시 로그아웃
        // eslint-disable-next-line no-console
        console.error('Token refresh failed:', refreshError);
        window.location.href = '/admin/auth/logout';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Orval에서 사용할 커스텀 인스턴스
export const customInstance = <T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig,
): Promise<T> => {
  const accessToken = GlobalAuthInfo.AccessToken;
  const controller = new AbortController();

  return AXIOS_INSTANCE({
    ...config,
    ...options,
    signal: controller.signal,
    headers: {
      ...config?.headers,
      ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    paramsSerializer: (params: Record<string, any>) => {
      const searchParams = new URLSearchParams();
      Object.keys(params).forEach((key) => {
        const param = params[key];
        // 빈 배열은 건너뛰기
        if (Array.isArray(param) && param.length === 0) {
          return;
        }
        if (Array.isArray(param)) {
          searchParams.append(key, param.join(','));
        } else {
          searchParams.append(key, param);
        }
      });
      return searchParams.toString();
    },
  }).then(({ data }) => data);
};

export type ErrorType<Error> = AxiosError<Error>;
